import { Link, useLocation } from "@tanstack/react-router";
import { Upload } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { cn } from "@ndd/ui";
import type { AlwaysAiUser } from "../../../../shared/api-types";
import { landingPathForRole } from "@/lib/permissions";
import { ShellHeader, type ShellLookSwitch } from "./shell-header";
import {
  SHELL_HEADER_GEOMETRY,
  ShellSqueezeProvider,
  useIsDesktop,
} from "./shell-layout";
import {
  SiderailAppointmentsSection,
  useAppointmentsViewScope,
} from "./siderail-appointments";
import {
  SiderailDrawer,
  SiderailScrim,
  SiderailToggles,
  useSiderailCollapse,
  useSiderailDrawer,
} from "./siderail-frame";
import {
  SiderailIcon,
  SiderailLabelClip,
  SiderailOwnRdSoonItems,
  SiderailTooltip,
} from "./siderail-parts";

// The RD app shell: the shared ShellHeader bar (siderail toggle, wordmark,
// breadcrumbs, Clerk profile) over a two-width siderail,
// 300px expanded or a 64px icon strip, which never fully disappears on desktop.
// Under 1024px the siderail becomes an overlay drawer that always renders the
// expanded layout.
//
// The bar is the same component the legacy shell mounts, so navigating between
// the two shells no longer moves the chrome. It replaced a 48px sand-100 strip
// that shared its background with the siderail for a seamless top edge; the
// bordered bar now reads as its own surface, matching /rds and every workspace
// surface. Wayfinding is the bar's: pages set their crumbs with BreadcrumbSetter.
//
// The siderail's frame (density, drawer, toggles), its Appointments entry and
// its presentational parts live in siderail-frame.tsx, siderail-appointments.tsx
// and siderail-parts.tsx so another shell can reuse them (NO-754). What stays
// here is the RD shell's own composition.

export function AppShell({
  user,
  lookSwitch,
  children,
}: {
  user: AlwaysAiUser;
  /** A flagged viewer on the classic look: the way back to the rail (NO-754). */
  lookSwitch?: ShellLookSwitch;
  children: ReactNode;
}): ReactElement {
  const location = useLocation();
  const pathname = location.pathname;
  const { collapsed, setAndStoreCollapsed, setSqueezed } = useSiderailCollapse(user.id);
  const isDesktop = useIsDesktop();
  const drawer = useSiderailDrawer(pathname, isDesktop);

  return (
    <div className="flex h-screen flex-col bg-background">
      <AppShellHeader
        user={user}
        lookSwitch={lookSwitch}
        collapsed={collapsed}
        drawer={drawer}
        // Negate the rendered state, not the stored one: while the
        // workspace drawer squeezes the siderail, a click reads as
        // "expand" and must store "expanded", never flip the RD's
        // preference behind a label that says otherwise.
        onToggleCollapsed={() => setAndStoreCollapsed(!collapsed)}
      />
      <div className="flex min-h-0 flex-1">
        <SiderailDrawer
          drawerRef={drawer.drawerRef}
          open={drawer.open}
          isDesktop={isDesktop}
          collapsed={collapsed}
          fixedTop={SHELL_HEADER_GEOMETRY.fixedTop}
        >
          <ShellSiderail
            user={user}
            pathname={pathname}
            collapsed={collapsed}
            isDesktop={isDesktop}
            drawerOpen={drawer.open}
            onExpand={() => setAndStoreCollapsed(false)}
          />
        </SiderailDrawer>
        <SiderailScrim
          open={drawer.open}
          fixedTop={SHELL_HEADER_GEOMETRY.fixedTop}
          onDismiss={() => drawer.setOpen(false)}
        />
        {/* `relative` makes this scroll container the containing block for
            absolutely positioned descendants — `sr-only` live regions are
            absolute — so they scroll with the canvas instead of escaping to
            the document and giving the page a second scrollbar. */}
        <main
          ref={drawer.mainRef}
          inert={!isDesktop && drawer.open}
          className="relative min-h-0 min-w-0 flex-1 overflow-y-auto"
        >
          <ShellSqueezeProvider value={setSqueezed}>{children}</ShellSqueezeProvider>
        </main>
      </div>
    </div>
  );
}

function AppShellHeader({
  user,
  lookSwitch,
  collapsed,
  drawer,
  onToggleCollapsed,
}: {
  user: AlwaysAiUser;
  lookSwitch?: ShellLookSwitch;
  collapsed: boolean;
  drawer: ReturnType<typeof useSiderailDrawer>;
  onToggleCollapsed: () => void;
}): ReactElement {
  // The shell only renders with rd-home on, so the landing resolves with the
  // flag considered enabled. The admin's RD picker is the Appointment Home
  // title (appointment-scope-selector.tsx), not shell chrome.
  return (
    <ShellHeader
      landingPath={landingPathForRole(user, true)}
      lookSwitch={lookSwitch}
      leading={
        <SiderailToggles
          collapsed={collapsed}
          drawerOpen={drawer.open}
          mobileToggleRef={drawer.mobileToggleRef}
          onToggleCollapsed={onToggleCollapsed}
          onToggleDrawer={() => drawer.setOpen((value) => !value)}
        />
      }
    />
  );
}

function ShellSiderail({
  user,
  pathname,
  collapsed,
  isDesktop,
  drawerOpen,
  onExpand,
}: {
  user: AlwaysAiUser;
  pathname: string;
  collapsed: boolean;
  isDesktop: boolean;
  drawerOpen: boolean;
  onExpand: () => void;
}): ReactElement {
  const { viewingOther } = useAppointmentsViewScope(user);

  return (
    <aside
      aria-label="Workspace navigation"
      className="flex h-full w-full flex-col overflow-y-auto overflow-x-hidden border-r border-border bg-muted px-3 pb-3.5 pt-2.5"
    >
      <nav className="flex flex-col gap-0.5">
        <SiderailAppointmentsSection
          user={user}
          pathname={pathname}
          collapsed={collapsed}
          isDesktop={isDesktop}
          drawerOpen={drawerOpen}
          onExpand={onExpand}
        />
        {/* Both promise things to the owning RD in second person, and neither is
            actionable for a visitor. */}
        {viewingOther ? null : <SiderailOwnRdSoonItems collapsed={collapsed} />}
      </nav>
      <div className="min-h-3 flex-1" />
      {/* Unqualified here it reads as "upload for this RD" and would file
          against the admin instead. */}
      {viewingOther ? null : (
        <SiderailUploadLink pathname={pathname} collapsed={collapsed} />
      )}
    </aside>
  );
}

// Upload lives only in the RD shell.
function SiderailUploadLink({
  pathname,
  collapsed,
}: {
  pathname: string;
  collapsed: boolean;
}): ReactElement {
  const current = pathname === "/transcripts/upload";

  return (
    <SiderailTooltip label="Upload a transcript" collapsed={collapsed}>
      <Link
        to="/transcripts/upload"
        aria-current={current ? "page" : undefined}
        className={cn(
          "flex h-10 items-center rounded-lg text-body transition-colors",
          current
            ? "bg-accent font-sans font-bold text-foreground"
            : "font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
        )}
      >
        <SiderailIcon
          icon={Upload}
          className="text-muted-foreground"
          iconClassName="size-4"
        />
        <SiderailLabelClip collapsed={collapsed}>Upload a transcript</SiderailLabelClip>
      </Link>
    </SiderailTooltip>
  );
}
